/**
 * @fileoverview SearchableCombobox component - a reusable dropdown with search functionality.
 * Provides a filtered list of items with customizable display and selection handling.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Props for the SearchableCombobox component.
 */
interface SearchableComboboxProps<T> {
  /** Array of items to display in the dropdown */
  readonly items: readonly T[];
  /** Whether the dropdown is currently open */
  readonly isOpen: boolean;
  /** Whether the combobox is disabled */
  readonly disabled?: boolean;
  /** Function to get the display name for an item */
  readonly getItemName: (item: T) => string;
  /** Function to get unique key for an item */
  readonly getItemKey: (item: T) => string | number;
  /** Function to render the trigger button content */
  readonly renderTrigger: () => ReactNode;
  /** Callback when an item is selected */
  readonly onSelect: (item: T) => void;
  /** Callback to toggle dropdown visibility */
  readonly onToggle: () => void;
  /** Optional: Show a "clear/none" option */
  readonly showClearOption?: boolean;
  /** Optional: Label for the clear option */
  readonly clearOptionLabel?: string;
  /** Optional: Callback when clear option is clicked */
  readonly onClear?: () => void;
  /** Optional: Placeholder text for search input */
  readonly searchPlaceholder?: string;
  /** Optional: Message when no results found */
  readonly noResultsMessage?: string;
  /** Optional: Whether showing placeholder state (darker color) vs selected item (lighter color) */
  readonly isPlaceholder?: boolean;
}

/**
 * SearchableCombobox provides a dropdown interface with search/filter capability.
 * Designed to be reusable across different dropdown use cases.
 *
 * @param props - Component props
 * @returns SearchableCombobox UI
 */
export default function SearchableCombobox<T>({
  items,
  isOpen,
  disabled = false,
  getItemName,
  getItemKey,
  renderTrigger,
  onSelect,
  onToggle,
  showClearOption = false,
  clearOptionLabel = "Keine Auswahl",
  onClear,
  searchPlaceholder = "Suchen...",
  noResultsMessage = "Keine Ergebnisse gefunden",
  isPlaceholder = false,
}: SearchableComboboxProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    } else {
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Filter items based on search term
  const filteredItems = items.filter((item) =>
    getItemName(item).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (item: T) => {
    onSelect(item);
    setSearchTerm("");
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      setSearchTerm("");
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && onToggle()}
        disabled={disabled}
        className={`w-full h-10 bg-[#fafafa] rounded-md px-4 py-1 text-sm ${
          isPlaceholder ? "text-[#a1a1aa]" : "text-[#71717a]"
        } text-left flex items-center gap-2 ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        {renderTrigger()}
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-[#e4e4e7] rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-[#e4e4e7]">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-8 bg-[#fafafa] rounded px-3 text-sm text-[#71717a] placeholder:text-[#a1a1aa] focus:outline-none focus:ring-1 focus:ring-[#d4d4d8]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Items List */}
          <div className="max-h-52 overflow-y-auto">
            {showClearOption && onClear && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full px-4 py-2 text-sm text-[#71717a] italic text-left hover:bg-[#f4f4f5] transition-colors"
              >
                {clearOptionLabel}
              </button>
            )}
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <button
                  key={getItemKey(item)}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full px-4 py-2 text-sm text-[#18181b] text-left hover:bg-[#f4f4f5] transition-colors"
                >
                  {getItemName(item)}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-[#71717a] italic">
                {noResultsMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
